import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { cn } from "@msuite/ui/utils";
import { inferResourceLinkType } from "@msuite/utils/resource-links";
import { useState } from "react";

/** Props Interface */
interface ResourceLinkCardProps {
	url: string;
	/** The label the link was given. The hostname stands in for it when it is empty. */
	name?: string | null;
}

export function ResourceLinkCard({ url, name }: ResourceLinkCardProps) {
	/** Context */
	const { t } = useTranslation();

	/** State */
	const [hasFaviconError, setHasFaviconError] = useState(false);

	/** Computed */
	const hostname = getHostname(url);
	const type = inferResourceLinkType(url);
	const faviconUrl = hostname
		? `https://www.google.com/s2/favicons?sz=64&domain=${hostname}`
		: null;
	const title = name || hostname;

	/** Guard */
	if (!hostname) return null;

	/** Render */
	return (
		<a
			className={cn(
				"card group flex flex-row items-center gap-3",
				"transition-all duration-150 ease-out",
				"hover:-translate-y-px hover:border-accent-500/50 hover:shadow-sm",
			)}
			target="_blank"
			rel="noreferrer"
			href={url}
			data-cy="resource-link-card"
		>
			<div className="flex size-9 shrink-0 items-center justify-center rounded-base border-[0.5px] border-color bg-primaryGray-50 dark:bg-primaryGray-800">
				{faviconUrl && !hasFaviconError ? (
					<img
						src={faviconUrl}
						alt=""
						className="size-5"
						onError={() => setHasFaviconError(true)}
					/>
				) : (
					<Icon
						name="Globe"
						size={16}
						className="text-primaryGray-400"
					/>
				)}
			</div>
			<div className="flex min-w-0 flex-1 flex-col gap-0.5">
				<div className="flex items-center gap-2">
					<p className="truncate text-sm font-medium text-color">{title}</p>
					{type && (
						<span className="shrink-0 rounded-base border-[0.5px] border-color bg-primaryGray-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primaryGray-500 dark:bg-primaryGray-800 dark:text-primaryGray-400">
							{t(`resource_links.types.${type}`)}
						</span>
					)}
				</div>
				<p className="truncate text-xs text-primaryGray-400">{url}</p>
			</div>
			<Icon
				name="ArrowUpRight"
				size={16}
				className={cn(
					"shrink-0 text-primaryGray-300",
					"transition-all duration-150 ease-out",
					"group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-accent-500",
				)}
			/>
		</a>
	);
}

function getHostname(url: string) {
	try {
		return new URL(url).hostname.replace(/^www\./, "");
	} catch {
		return null;
	}
}
